import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DISPLAY_MAX, GENERATION_LABEL_MAX } from '../_lib/generationForms';
import {
  MARKET_OPTIONS,
  marketName,
  RESTYLING_OPTIONS,
  restylingName,
} from '../_lib/generationLabel';
import type { GenerationForm } from '../_lib/useGenerationForm';

/**
 * «Поколение №» · «Рестайлинг» · «Рынок» · «Название» of the generation
 * dialog (AUT-450 / VG-03). «Название» is the FULL stored label (counter
 * x / 40); on an add form «Рестайлинг» / «Рынок» re-compose it until it is
 * edited by hand (`useGenerationForm`). A display over 40 (client preview or
 * the server's 422) reads «В отчёте будет N символов».
 */
const K = 'vehicleCatalog.generations';
const NUMBER_MAX_LENGTH = 2;

const labelHelper = (g: GenerationForm, t: TFunction, zodKey: string | undefined): string => {
  if (g.preview?.over) {
    return t(`${K}.reportTooLong`, { count: g.preview.length, max: DISPLAY_MAX });
  }
  // The server's 422 is the backstop — its count may differ from ours.
  if (g.serverError?.kind === 'tooLong') {
    return t(`${K}.reportTooLongServer`, { max: DISPLAY_MAX });
  }
  return zodKey ? t(zodKey) : t(`${K}.fields.labelHint`);
};

const LabelField = ({ g, disabled }: { g: GenerationForm; disabled: boolean }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Controller
      control={g.form.control}
      name="label"
      render={({ field, fieldState }) => {
        const tooLong = g.serverError?.kind === 'tooLong' || (g.preview?.over ?? false);
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {t(`${K}.fields.counter`, { len: field.value.length, max: GENERATION_LABEL_MAX })}
              </Typography>
            </Box>
            <TextField
              inputRef={field.ref}
              fullWidth
              size="small"
              required
              label={t(`${K}.fields.label`)}
              value={field.value}
              disabled={disabled}
              onBlur={field.onBlur}
              onChange={(e) => g.setLabel(e.target.value)}
              error={tooLong || fieldState.error !== undefined}
              helperText={labelHelper(g, t, fieldState.error?.message)}
              slotProps={{ htmlInput: { maxLength: GENERATION_LABEL_MAX } }}
            />
          </Box>
        );
      }}
    />
  );
};

const marketChoices = (current: string): readonly string[] =>
  (MARKET_OPTIONS as readonly string[]).includes(current)
    ? MARKET_OPTIONS
    : [...MARKET_OPTIONS, current];

/** «Рестайлинг» + «Рынок» — each change may re-compose the label (add form). */
const KeySelects = ({ g, disabled }: { g: GenerationForm; disabled: boolean }): ReactElement => {
  const { t } = useTranslation();
  const { control } = g.form;
  return (
    <>
      <Controller
        control={control}
        name="restyling"
        render={({ field }) => (
          <TextField
            select
            id="generation-restyling"
            size="small"
            label={t(`${K}.fields.restyling`)}
            value={field.value}
            disabled={disabled}
            onChange={(e) => g.setRestyling(Number(e.target.value))}
          >
            {RESTYLING_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {restylingName(t, n)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        control={control}
        name="market"
        render={({ field }) => (
          <TextField
            select
            id="generation-market"
            size="small"
            label={t(`${K}.fields.market`)}
            value={field.value}
            disabled={disabled}
            onChange={(e) => g.setMarket(e.target.value)}
            helperText={t(`${K}.fields.marketHint`)}
            sx={{ gridColumn: '1 / -1', maxWidth: '50%' }}
          >
            {marketChoices(field.value).map((code) => (
              <MenuItem key={code} value={code}>
                {marketName(t, code)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </>
  );
};

export const GenerationFields = ({
  g,
  disabled,
}: {
  g: GenerationForm;
  disabled: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const { control } = g.form;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <Controller
        control={control}
        name="generation"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            size="small"
            required
            label={t(`${K}.fields.generation`)}
            disabled={disabled}
            onChange={(e) => {
              g.clearServerError('generationConflict');
              field.onChange(e.target.value);
            }}
            error={fieldState.error !== undefined}
            helperText={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: NUMBER_MAX_LENGTH } }}
          />
        )}
      />
      <KeySelects g={g} disabled={disabled} />
      <Box sx={{ gridColumn: '1 / -1' }}>
        <LabelField g={g} disabled={disabled} />
      </Box>
    </Box>
  );
};
